import type { FeatureContext } from './featureContext';
import { proximityCueVolume } from './cueVolume';

// Loudness ceilings for The King's Justice cues (PRD #361, ADR-0194), proximity-
// scaled like the vortex gust: the halberd's execution wack punches like a duel
// death; the smoke poof mixes like a door.
const HALBERD_HIT_VOLUME = 0.9;
const POOF_VOLUME = 0.7;

/**
 * The King's Justice presentation (PRD #361, ADR-0194): the thief's-corpse loot
 * double-click, the guard's halberd wack, and the teleport smoke poof.
 */
export function installJustice(ctx: FeatureContext): void {
  // Double-clicking the King's Justice thief's corpse sends a LootCorpse up the
  // socket - intent only. The server rolls the 2d6 pile (never the client),
  // enforces the Chebyshev-2 range against the looter's authoritative tile, and
  // settles the first-loot race; a premature or out-of-range double-click fizzles
  // silently.
  ctx.onRendererBuilt((renderer) => {
    renderer.onCorpseLoot = (corpse) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'LootCorpse', entity: playerId, corpse });
      }
    };
  });

  // The King's Justice halberd wack: the guard's one-shot `attack-2h` swing sounds
  // the polearm hit, proximity-scaled to the local player (the execution can happen
  // anywhere around the bank). Keyed off the clip name - only the event guard ever
  // plays it.
  ctx.events.on('actionAnimation', (msg) => {
    if (msg.clip !== 'attack-2h') return;
    const volume = proximityCueVolume(ctx.world(), ctx.playerId(), msg.entityId, HALBERD_HIT_VOLUME);
    if (volume > 0) ctx.audio.play({ sound: 'halberd-hit', volume });
  });

  // The King's Justice smoke poof: one ephemeral message drives both the visual
  // (the baked `poof` effect strip at the tile/level) and the audio (the
  // teleport-poof, proximity-scaled to the local player like a rat squeal), so
  // sight and sound can never disagree. Hidden-tab frames spawn no visual (the
  // rAF-driven FX layer would only age it late); the sound still plays.
  ctx.events.on('justiceFx', (msg) => {
    if (!ctx.ui.hidden) ctx.renderer()?.playPoof(msg.x, msg.y, msg.z);
    const volume = proximityCueVolume(
      ctx.world(),
      ctx.playerId(),
      { x: msg.x, y: msg.y },
      POOF_VOLUME,
    );
    if (volume > 0) ctx.audio.play({ sound: 'poof', volume });
  });
}
